import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, InputAdornment, MenuItem, Paper, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { CalendarMonthOutlined as CalendarIcon, LayersOutlined as MultiIcon, AssessmentOutlined as ReportIcon } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { usePagePermissions, useAuth } from '../context/AuthContext';

export default function HardcopySheetManagement() {
  const navigate = useNavigate();
  const { canCreate } = usePagePermissions('hardcopy_sheet_management');
  const { user } = useAuth();
  const [appCodes, setAppCodes] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [firmList, setFirmList] = useState([]);
  const [articleList, setArticleList] = useState([]);
  const [form, setForm] = useState({
    userCode: '',
    billDate: '',
    vendorCode: '',
    firmCode: '',
    invoiceNumber: '',
    article: '',
    gstPct: '',
    billAmount: '',
    freightCharges: '',
    taxableAmount: '',
    qty: '',
    cgst: '',
    sgst: '',
    igst: '',
    ewayBill: '',
    rate: '',
  });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const dateRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const headers = { Authorization: `Bearer ${token}` };

        const isSuper = user?.role === 'superadmin';
        if (isSuper) {
          const resCodes = await fetch(`${API_BASE_URL}/api/user-application-codes`, { headers });
          const dataCodes = await resCodes.json().catch(() => ({}));
          const codes = Array.isArray(dataCodes.items)
            ? dataCodes.items.map((it) => it.code).filter(Boolean).sort()
            : [];
          setAppCodes(codes);
        } else {
          setAppCodes([]);
        }

        const resArticles = await fetch(`${API_BASE_URL}/api/articles`, { headers });
        const dataArticles = await resArticles.json().catch(() => ({}));
        setArticleList(Array.isArray(dataArticles.items) ? dataArticles.items : []);

        const resVendors = await fetch(`${API_BASE_URL}/api/vendors`, { headers });
        const dataVendors = await resVendors.json().catch(() => ({}));
        const allItems = Array.isArray(dataVendors.items) ? dataVendors.items : [];
        
        // Filter into Vendors and Firms
        // Deduplicate by firmVendorCode
        const vendors = allItems.filter(i => String(i.firmVendor || '').trim().toLowerCase() === 'vendor');
        const firms = allItems.filter(i => String(i.firmVendor || '').trim().toLowerCase() === 'firm');

        let uniqueVendors = Array.from(new Map(vendors.map(item => [String(item.firmVendorCode || '').trim(), item])).values());
        const uniqueFirms = Array.from(new Map(firms.map(item => [String(item.firmVendorCode || '').trim(), item])).values());
        
        const userAssociations = user?.association || [];

        if (!isSuper && user?.application_code) {
          setForm(prev => ({ ...prev, userCode: user.application_code }));
        }

        if (!isSuper) {
          // Filter Vendors
          uniqueVendors = uniqueVendors.filter(v => userAssociations.includes(v.firmVendorCode));
          
          // Filter Firms
          uniqueFirms = uniqueFirms.filter(f => userAssociations.includes(f.firmVendorCode));
        }
        
        uniqueVendors.sort((a, b) => String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || '')));
        uniqueFirms.sort((a, b) => String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || '')));

        setVendorList(uniqueVendors);
        setFirmList(uniqueFirms);
      } catch {
        setAppCodes([]);
        setArticleList([]);
        setVendorList([]);
        setFirmList([]);
      }
    };
    load();
  }, [user]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      
      let newGstPct = next.gstPct;

      if (field === 'article') {
        const selectedArticle = articleList.find(a => a.item === value);
        if (selectedArticle) {
          newGstPct = String(selectedArticle.gstRatio || '');
          next.gstPct = newGstPct;
        }
      }

      // Recalculate taxable amount if billAmount OR gstPct changes
      // Formula: (Bill Amount * 100) / (100 + GST%)
      const bill = Number(next.billAmount) || 0;
      const gstVal = Number(newGstPct) || 0;
      
      // Only calculate if we have a bill amount (GST can be 0)
      if (bill > 0) {
        const denominator = 100 + gstVal;
        const calcTaxable = (bill * 100) / denominator;
        next.taxableAmount = String(calcTaxable.toFixed(2));
      } else if (field === 'billAmount') {
        // Clear taxable if bill amount is cleared
        next.taxableAmount = '';
      }

      // Calculate taxes based on Vendor GSTIN
      const currentVendorCode = field === 'vendorCode' ? value : next.vendorCode;
      const currentVendor = vendorList.find(v => v.firmVendorCode === currentVendorCode);
      const vendorGst = currentVendor?.gstNumber || '';
      const isIntraState = vendorGst.startsWith('06');
      
      const taxableVal = Number(next.taxableAmount) || 0;
      const gstRate = Number(next.gstPct) || 0;
      
      // Total Tax Amount = Taxable * (GST% / 100)
      // Or Bill Amount - Taxable Amount (but bill amount includes round off, so calculate from taxable)
      // Actually Taxable was derived from Bill, so Taxable * Rate is correct.
      
      // But wait, if Taxable = (Bill * 100) / (100+Rate), then Tax = Bill - Taxable.
      // Let's use (Bill - Taxable) to be exact with the Bill Amount.
      // Or just Taxable * Rate / 100.
      // User formula: (Bill * 100) / (100+Rate)
      // Example: Bill 118, Rate 18. Taxable = 100. Tax = 18.
      // 100 * 18/100 = 18.
      // Example: Bill 105, Rate 5. Taxable = 100. Tax = 5.
      // 100 * 5/100 = 5.
      // So Taxable * Rate / 100 is correct.
      
      const totalTax = taxableVal * (gstRate / 100);
      
      if (isIntraState) {
        // CGST + SGST (half each)
        const halfTax = totalTax / 2;
        next.cgst = halfTax > 0 ? halfTax.toFixed(2) : '';
        next.sgst = halfTax > 0 ? halfTax.toFixed(2) : '';
        next.igst = '';
      } else {
        // IGST (full)
        next.cgst = '';
        next.sgst = '';
        next.igst = totalTax > 0 ? totalTax.toFixed(2) : '';
      }

      const gst = Number(next.gstPct) || 0;
      const taxable = Number(next.taxableAmount) || 0;
      const isRequired = taxable >= 50000;
      next.ewayBill = isRequired ? 'Required' : 'Not Required';
      // const igstVal = (gst / 100) * taxable; // Removed old logic
      // next.igst = String(igstVal || ''); // Removed old logic
      return next;
    });
  };

  const computedTotals = useMemo(() => {
    const bill = Number(form.billAmount) || 0;
    const freight = Number(form.freightCharges) || 0;
    const taxable = Number(form.taxableAmount) || 0;
    const qty = Number(form.qty) || 0;
    const rate = qty > 0 ? taxable / qty : '';
    return { bill, freight, taxable, qty, rate };
  }, [form.billAmount, form.freightCharges, form.taxableAmount, form.qty]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, rate: computedTotals.rate ? String(computedTotals.rate.toFixed(2)) : '' }));
  }, [computedTotals.rate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) {
      setToast({ open: true, message: 'You do not have permission to create hardcopy sheets', severity: 'error' });
      return;
    }
    if (!form.userCode || !form.billDate) {
      setToast({ open: true, message: 'Application User Code aur Date required', severity: 'error' });
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || '';
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const res = await fetch(`${API_BASE_URL}/api/hardcopy-sheets`, {
        method: 'POST',
        headers,
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setToast({ open: true, message: 'Hardcopy Sheet Saved Successfully', severity: 'success' });
        // Optional: Reset form or reload list? For now just keep form as is or clear critical fields
        setForm({
            userCode: user?.role === 'superadmin' ? '' : (user?.application_code || ''),
            billDate: '',
            vendorCode: '',
            firmCode: '',
            invoiceNumber: '',
            article: '',
            gstPct: '',
            billAmount: '',
            freightCharges: '',
            taxableAmount: '',
            qty: '',
            cgst: '',
            sgst: '',
            igst: '',
            ewayBill: '',
            rate: '',
        });
      } else {
        setToast({ open: true, message: data.error || 'Failed to save', severity: 'error' });
      }
    } catch (err) {
      console.error(err);
      setToast({ open: true, message: 'Network error', severity: 'error' });
    }
  };

  return (
    <Layout title="Hardcopy Sheet Management">
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 18px 45px rgba(15,23,42,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: 0.3 }}>
              Hardcopy Sheet Management
            </Typography>
            {canCreate && (
              <Button
                component={Link}
                to="/multi-hardcopy-sheet"
                startIcon={<MultiIcon />}
                variant="contained"
                color="success"
              >
                Multi Hardcopy Sheet
              </Button>
            )}
          </Box>

          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                columnGap: 6,
                rowGap: 3,
              }}
            >
              <TextField
                label="Application User Code"
                select={user?.role === 'superadmin'}
                value={form.userCode}
                onChange={handleChange('userCode')}
                variant="standard"
                fullWidth
                InputProps={{ readOnly: user?.role !== 'superadmin' }}
              >
                {user?.role === 'superadmin' ? appCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                )) : null}
              </TextField>
              <TextField
                label="Date of Bill Received"
                type="date"
                value={form.billDate}
                onChange={handleChange('billDate')}
                variant="standard"
                fullWidth
                inputRef={dateRef}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="From Vendor Code"
                select
                value={form.vendorCode}
                onChange={handleChange('vendorCode')}
                variant="standard"
                fullWidth
              >
                {vendorList.map((v) => (
                  <MenuItem key={v.id} value={v.firmVendorCode}>
                    {v.firmVendorCode}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="To Firm Code"
                select
                value={form.firmCode}
                onChange={handleChange('firmCode')}
                variant="standard"
                fullWidth
              >
                {firmList.map((f) => (
                  <MenuItem key={f.id} value={f.firmVendorCode}>
                    {f.firmVendorCode}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Article"
                select
                value={form.article}
                onChange={handleChange('article')}
                variant="standard"
                fullWidth
              >
                {articleList.map((a) => (
                  <MenuItem key={a.id} value={a.item}>
                    {a.item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Applicable GST %"
                value={form.gstPct}
                onChange={handleChange('gstPct')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Invoice Number"
                value={form.invoiceNumber}
                onChange={handleChange('invoiceNumber')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Bill Amount"
                value={form.billAmount}
                onChange={handleChange('billAmount')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Freight & forwarding charges / Loading & Unloading"
                value={form.freightCharges}
                onChange={handleChange('freightCharges')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Taxable Amount"
                value={form.taxableAmount}
                onChange={handleChange('taxableAmount')}
                variant="standard"
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Qty"
                  value={form.qty}
                  onChange={handleChange('qty')}
                  variant="standard"
                  fullWidth
                />
              </Stack>
              <TextField
                label="CGST"
                value={form.cgst}
                onChange={handleChange('cgst')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="SGST"
                value={form.sgst}
                onChange={handleChange('sgst')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="IGST"
                value={form.igst}
                onChange={handleChange('igst')}
                variant="standard"
                fullWidth
              />
              <TextField
                label="E-Way Bill (Auto Calculated)"
                value={form.ewayBill}
                onChange={handleChange('ewayBill')}
                variant="standard"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Rate"
                value={form.rate}
                onChange={handleChange('rate')}
                variant="standard"
                fullWidth
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!canCreate}
                sx={{
                  px: 5,
                  py: 1.2,
                  borderRadius: 0.75,
                  bgcolor: '#8e24aa',
                  '&:hover': { bgcolor: '#7b1fa2' },
                  boxShadow: '0 8px 18px rgba(126,34,206,0.35)',
                  letterSpacing: 1.5,
                }}
              >
                SUBMIT
                <Box component="span" sx={{ ml: 1.5, fontSize: 18, lineHeight: 1 }}>
                  ▶
                </Box>
              </Button>
            </Box>
          </Box>
        </Paper>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
